import { useEffect, useState, useRef, useCallback } from 'react';
import { doc, onSnapshot, updateDoc, deleteDoc, type Timestamp } from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import { useAuth } from '@/context/AuthContext';
import { useMountedRef } from './useMountedRef';

/**
 * Key for storing the acknowledged force refresh ID.
 * This prevents showing the same force refresh after page reload.
 */
const ACKNOWLEDGED_ID_KEY = 'spirit_force_refresh_ack_id';

export interface ForceRefreshState {
  /** Whether a force refresh is currently required */
  isRequired: boolean;
  /** The reason for the force refresh (if provided) */
  reason: string | null;
  /** When the force refresh becomes effective (for scheduled refreshes) */
  effectiveAt: Date | null;
  /** Whether this is a scheduled refresh that hasn't triggered yet */
  isScheduled: boolean;
  /** Time remaining until scheduled refresh (in seconds) */
  timeRemaining: number | null;
  /** Source of the force refresh: 'global' for all users, 'user' for specific user */
  source: 'global' | 'user' | null;
  /** Whether to automatically refresh the browser */
  autoRefresh: boolean;
  /** Delay before auto-refresh (for immediate mode) */
  autoRefreshDelay: number;
  /** The unique ID of this force refresh trigger */
  refreshId: string | null;
}

/**
 * Hook that listens for admin-triggered force refresh commands.
 *
 * Monitors both:
 * - Global force refresh: `config/force_refresh` (affects all users)
 * - Per-user force refresh: `users/{userId}/force_refresh/current` (affects specific user)
 *
 * Returns state about the force refresh requirement, which should be used
 * to display a non-dismissible modal.
 */
export function useForceRefresh(): ForceRefreshState {
  const { authState } = useAuth();
  const isMountedRef = useMountedRef();
  const [state, setState] = useState<ForceRefreshState>({
    isRequired: false,
    reason: null,
    effectiveAt: null,
    isScheduled: false,
    timeRemaining: null,
    source: null,
    autoRefresh: false,
    autoRefreshDelay: 10,
    refreshId: null,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Check if this refresh ID has already been acknowledged
  const isAcknowledged = useCallback((refreshId: string | undefined): boolean => {
    if (!refreshId) return false;
    const acknowledgedId = localStorage.getItem(ACKNOWLEDGED_ID_KEY);
    return acknowledgedId === refreshId;
  }, []);

  // Update time remaining for scheduled refreshes
  const updateTimeRemaining = useCallback(
    (effectiveDate: Date | null) => {
      if (!isMountedRef.current) return;

      if (!effectiveDate) {
        setState((prev) => ({ ...prev, timeRemaining: null }));
        return;
      }

      const now = Date.now();
      const remaining = Math.max(0, Math.floor((effectiveDate.getTime() - now) / 1000));

      if (isMountedRef.current) {
        setState((prev) => ({
          ...prev,
          timeRemaining: remaining,
          isScheduled: remaining > 0,
        }));
      }

      // If time is up, trigger the refresh
      if (remaining === 0 && isMountedRef.current) {
        setState((prev) => ({ ...prev, isScheduled: false }));
      }
    },
    [isMountedRef]
  );

  useEffect(() => {
    const userId = authState.status === 'authenticated' ? authState.user.uid : null;

    // Listener for global force refresh
    const globalRef = doc(db, 'artifacts', appId, 'config', 'force_refresh');
    const unsubscribeGlobal = onSnapshot(
      globalRef,
      (snapshot) => {
        if (!isMountedRef.current) return;

        if (!snapshot.exists()) {
          // Only clear if this was the active source
          if (isMountedRef.current) {
            setState((prev) =>
              prev.source === 'global'
                ? {
                    isRequired: false,
                    reason: null,
                    effectiveAt: null,
                    isScheduled: false,
                    timeRemaining: null,
                    source: null,
                    autoRefresh: false,
                    autoRefreshDelay: 10,
                    refreshId: null,
                  }
                : prev
            );
          }
          return;
        }

        const data = snapshot.data();

        // Check if there's a valid trigger
        const triggeredAt = data?.triggeredAt as Timestamp | undefined;
        if (!triggeredAt) {
          return;
        }

        // Use ID if present, otherwise generate from timestamp (for backwards compatibility)
        const refreshId = (data?.id as string | undefined) || `legacy_${triggeredAt.toMillis()}`;

        // Check if already acknowledged
        if (isAcknowledged(refreshId)) {
          return;
        }

        // Check if scheduled for future
        const effectiveAt = data?.effectiveAt as Timestamp | undefined;
        const effectiveDate = effectiveAt?.toDate() || triggeredAt.toDate();
        const now = Date.now();
        const isScheduled = effectiveDate.getTime() > now;

        if (isMountedRef.current) {
          setState({
            isRequired: true,
            reason: data?.reason || null,
            effectiveAt: effectiveDate,
            isScheduled,
            timeRemaining: null,
            source: 'global',
            autoRefresh: data?.autoRefresh ?? true,
            autoRefreshDelay: data?.autoRefreshDelay ?? 10,
            refreshId: refreshId || null,
          });
        }

        // Start countdown if scheduled
        if (isScheduled && effectiveDate) {
          updateTimeRemaining(effectiveDate);
          // Clear any existing interval before creating a new one
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          intervalRef.current = setInterval(() => {
            updateTimeRemaining(effectiveDate);
          }, 1000);
        }
      },
      (error) => {
        console.warn('Force refresh global listener error:', error);
      }
    );

    // Listener for per-user force refresh
    let unsubscribeUser: (() => void) | null = null;
    if (userId) {
      const userRef = doc(db, 'artifacts', appId, 'users', userId, 'force_refresh', 'current');
      unsubscribeUser = onSnapshot(
        userRef,
        (snapshot) => {
          if (!isMountedRef.current) return;

          if (!snapshot.exists()) {
            // Only clear if this was the active source
            if (isMountedRef.current) {
              setState((prev) =>
                prev.source === 'user'
                  ? {
                      isRequired: false,
                      reason: null,
                      effectiveAt: null,
                      isScheduled: false,
                      timeRemaining: null,
                      source: null,
                      autoRefresh: false,
                      autoRefreshDelay: 10,
                      refreshId: null,
                    }
                  : prev
              );
            }
            return;
          }

          const data = snapshot.data();

          // Check if already acknowledged in Firestore
          if (data?.acknowledged) {
            return;
          }

          // Check if there's a valid trigger
          const triggeredAt = data?.triggeredAt as Timestamp | undefined;
          if (!triggeredAt) {
            return;
          }

          // Use ID if present, otherwise generate from timestamp (for backwards compatibility)
          const refreshId =
            (data?.id as string | undefined) || `legacy_user_${triggeredAt.toMillis()}`;

          // Check if already acknowledged locally
          if (isAcknowledged(refreshId)) {
            return;
          }

          // Check if scheduled for future
          const effectiveAt = data?.effectiveAt as Timestamp | undefined;
          const effectiveDate = effectiveAt?.toDate() || triggeredAt.toDate();
          const now = Date.now();
          const isScheduled = effectiveDate.getTime() > now;

          // User-specific takes priority over global
          if (isMountedRef.current) {
            setState({
              isRequired: true,
              reason: data?.reason || null,
              effectiveAt: effectiveDate,
              isScheduled,
              timeRemaining: null,
              source: 'user',
              autoRefresh: data?.autoRefresh ?? true,
              autoRefreshDelay: data?.autoRefreshDelay ?? 10,
              refreshId: refreshId || null,
            });
          }

          // Start countdown if scheduled
          if (isScheduled && effectiveDate) {
            updateTimeRemaining(effectiveDate);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
            intervalRef.current = setInterval(() => {
              updateTimeRemaining(effectiveDate);
            }, 1000);
          }
        },
        (error) => {
          console.warn('Force refresh user listener error:', error);
        }
      );
    }

    return () => {
      unsubscribeGlobal();
      if (unsubscribeUser) {
        unsubscribeUser();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [authState, isAcknowledged, updateTimeRemaining, isMountedRef]);

  return state;
}

/**
 * Acknowledge a force refresh (used when user clicks refresh).
 * This prevents the modal from showing again after page reload.
 * Also deletes/marks the Firestore document to prevent loops.
 */
export async function acknowledgeForceRefresh(
  source: 'global' | 'user',
  refreshId: string | null,
  userId?: string
): Promise<void> {
  // Store the acknowledged refresh ID (or a marker if no ID)
  localStorage.setItem(ACKNOWLEDGED_ID_KEY, refreshId || `ack_${Date.now()}`);

  if (source === 'global') {
    // Delete the global force refresh document to prevent loops
    const globalRef = doc(db, 'artifacts', appId, 'config', 'force_refresh');
    try {
      await deleteDoc(globalRef);
    } catch (error) {
      // May not have permission, that's fine - localStorage will handle it
      console.warn('Could not delete global force refresh:', error);
    }
  } else if (source === 'user' && userId) {
    // Delete the user-specific force refresh document
    const userRef = doc(db, 'artifacts', appId, 'users', userId, 'force_refresh', 'current');
    try {
      await deleteDoc(userRef);
    } catch (error) {
      // Try marking as acknowledged if delete fails
      try {
        await updateDoc(userRef, { acknowledged: true });
      } catch {
        console.warn('Could not acknowledge force refresh:', error);
      }
    }
  }
}
